import { TimeWindow } from '../types';

/**
 * Retourne les minutes écoulées depuis minuit dans le fuseau horaire donné.
 * Essaie plusieurs méthodes pour maximiser la compatibilité (Samsung WebView, Tizen, etc.).
 * En dernier recours, utilise l'heure locale de l'appareil.
 */
function getCurrentTimeMinutes(timezone: string, currentDate: Date): number {
  // Méthode 1 : formatToParts — la plus fiable, mais absente sur certains WebView anciens
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const parts = formatter.formatToParts(currentDate);
    const hour = parts.find((p) => p.type === 'hour')?.value;
    const minute = parts.find((p) => p.type === 'minute')?.value;
    if (hour !== undefined && minute !== undefined) {
      return parseInt(hour, 10) * 60 + parseInt(minute, 10);
    }
  } catch (_) {
    // méthode non supportée, on tente la suivante
  }

  // Méthode 2 : toLocaleString — support plus large (Chromium 24+, Samsung WebView)
  try {
    const str = currentDate.toLocaleString('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const match = str.match(/(\d{1,2}):(\d{2})/);
    if (match) {
      return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
    }
  } catch (_) {
    // méthode non supportée, on tente la suivante
  }

  // Méthode 3 : heure locale de l'appareil (pas de conversion timezone, mais le filtre reste actif)
  return currentDate.getHours() * 60 + currentDate.getMinutes();
}

/**
 * Vérifie si une communication est dans sa fenêtre horaire active.
 */
export function isInTimeWindow(
  timeWindow: TimeWindow | null | undefined,
  currentDate: Date = new Date(),
): boolean {
  if (!timeWindow) {
    return true;
  }

  const [startHour, startMinute] = timeWindow.startTime.split(':').map(Number);
  const [endHour, endMinute] = timeWindow.endTime.split(':').map(Number);
  const startMinutes = startHour * 60 + startMinute;
  const endMinutes = endHour * 60 + endMinute;
  const currentMinutes = getCurrentTimeMinutes(timeWindow.timezone, currentDate);

  // Fenêtre traversant minuit (ex: 22:00 - 06:00)
  if (endMinutes < startMinutes) {
    return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
  }

  return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
}

/**
 * Filtre une liste de communications selon leur fenêtre horaire
 * @param communications - Liste des communications
 * @param currentDate - La date courante (par défaut : maintenant)
 * @returns Liste des communications filtrées
 */
export function filterByTimeWindow<T extends { timeWindow?: TimeWindow }>(
  communications: T[],
  currentDate: Date = new Date(),
): T[] {
  return communications.filter((comm) =>
    isInTimeWindow(comm.timeWindow, currentDate),
  );
}
